<template>
  <div class="dashboard-container">
    <!-- Header Section -->
    <header class="dashboard-header">
      <div class="header-content">
        <div class="header-left">
          <button class="back-button" @click="goBack" aria-label="Go back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="title-section">
            <h1 class="page-title">Dashboard</h1>
            <p class="page-subtitle">Property analytics and insights</p>
          </div>
        </div>
        <div class="header-actions">
          <button class="action-btn" @click="refreshData" :disabled="isRefreshing" title="Refresh data">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" :class="{ 'spinning': isRefreshing }">
              <path d="M1 4V10H7M23 20V14H17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M3.51 15A9 9 0 0 0 18.36 18.36L23 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="action-btn" @click="exportData" title="Export data">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 15V19A2 2 0 0 1 19 21H5A2 2 0 0 1 3 19V15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <polyline points="7,10 12,15 17,10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="main-content">
      <!-- KPI Overview Section -->
      <section class="metrics-section">
        <div class="metrics-grid">
          <div class="metric-card metric-card--inventory">
            <div class="metric-header">
              <div class="metric-icon-wrapper">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 9L12 2L21 9V20A2 2 0 0 1 19 22H5A2 2 0 0 1 3 20V9Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
            <div class="metric-body">
              <div class="metric-value">{{ calculateTotalRooms() }}</div>
              <div class="metric-label">Total Inventory</div>
            </div>
          </div>

          <div class="metric-card metric-card--occupancy">
            <div class="metric-header">
              <div class="metric-icon-wrapper">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
            <div class="metric-body">
              <div class="metric-value">{{ calculateOccupancyRate() }}%</div>
              <div class="metric-label">Occupancy Rate</div>
            </div>
          </div>

          <div class="metric-card metric-card--revenue">
            <div class="metric-header">
              <div class="metric-icon-wrapper">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
            <div class="metric-body">
              <div class="metric-value">₹{{ calculateRevenue() }}</div>
              <div class="metric-label">Total Revenue</div>
            </div>
          </div>

          <div class="metric-card metric-card--guests" v-if="dashboardData.totalOnboarded > 0 || dashboardData.activeOnboarded > 0">
            <div class="metric-header">
              <div class="metric-icon-wrapper">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 21V19A4 4 0 0 0 12 15H5A4 4 0 0 0 1 19V21M12.5 7.5A4 4 0 1 1 4.5 7.5A4 4 0 0 1 12.5 7.5ZM20 8V14L23 11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
            <div class="metric-body">
              <div class="metric-value">{{ dashboardData.activeOnboarded }}/{{ dashboardData.totalOnboarded }}</div>
              <div class="metric-label">Active Guests</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Analytics Charts Section -->
      <section class="chart-section" v-if="dashboardData.weeklyGraph && dashboardData.weeklyGraph.length > 0">
        <div class="chart-container">
          <div class="chart-header">
            <div>
              <h2 class="section-title">Weekly Trends</h2>
              <p class="section-description">Track performance over time</p>
            </div>
            <div class="chart-toggle">
              <button 
                class="toggle-btn" 
                :class="{ 'active': chartView === 'occupancy' }" 
                @click="chartView = 'occupancy'"
              >
                Occupancy
              </button>
              <button 
                class="toggle-btn" 
                :class="{ 'active': chartView === 'revenue' }" 
                @click="chartView = 'revenue'"
              >
                Revenue
              </button>
            </div>
          </div>
          <div class="chart-body">
            <div class="chart-wrapper">
              <div class="chart-bars">
                <div 
                  v-for="(bar, index) in currentChartData" 
                  :key="`${chartView}-${index}`" 
                  class="chart-bar" 
                  :style="{ height: bar.height + '%', background: bar.color }"
                >
                  <div class="bar-tooltip">{{ bar.label }}: {{ bar.value }}</div>
                </div>
              </div>
              <div class="chart-axis">
                <span v-for="label in currentChartLabels" :key="label" class="axis-label">{{ label }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Room Status Overview -->
      <section class="inventory-section" v-if="objectSpaces && objectSpaces.length > 0">
        <div class="section-header">
          <div>
            <h2 class="section-title">Room Inventory</h2>
            <p class="section-description">Detailed breakdown by category</p>
          </div>
        </div>
      </section>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-container">
        <div v-for="n in 3" :key="n" class="space-category-skeleton">
          <div class="skeleton-title"></div>
          <div class="cards-grid">
            <div v-for="i in 4" :key="i" class="card-skeleton"></div>
          </div>
        </div>
      </div>

      <!-- Content with Data -->
      <div v-else-if="objectSpaces.length > 0" class="inventory-content">
        <div 
          v-for="space in objectSpaces" 
          :key="space.objectSpaceId" 
          class="inventory-card"
        >
          <div class="inventory-header">
            <div>
              <h3 class="inventory-title">{{ getCategoryTitle(space) }}</h3>
              <span class="inventory-type">{{ space.objectType }}</span>
            </div>
          </div>

          <!-- Dormitory/Hostel Layout -->
          <div v-if="isDormitoryOrHostel(space)" class="status-grid">
            <div class="status-card status-card--total">
              <div class="status-icon">
                <img src="@/assets/purple-bed.png" alt="Total beds" />
              </div>
              <div class="status-info">
                <div class="status-value">{{ formatNumber(space.totalNumberOfBed) }}</div>
                <div class="status-label">Total Beds</div>
              </div>
            </div>

            <div class="status-card status-card--available">
              <div class="status-icon">
                <img src="@/assets/green-bed.png" alt="Available beds" />
              </div>
              <div class="status-info">
                <div class="status-value">{{ formatNumber(space.statusCounts.AVAILABLE) }}</div>
                <div class="status-label">Available</div>
              </div>
            </div>

            <div class="status-card status-card--occupied">
              <div class="status-icon">
                <img src="@/assets/red-bed.png" alt="Occupied beds" />
              </div>
              <div class="status-info">
                <div class="status-value">{{ formatNumber(space.statusCounts.BOOKED) }}</div>
                <div class="status-label">Occupied</div>
              </div>
            </div>

            <div class="status-card status-card--held">
              <div class="status-icon">
                <img src="@/assets/orange-bed.png" alt="Held beds" />
              </div>
              <div class="status-info">
                <div class="status-value">{{ formatNumber(space.statusCounts.HELD) }}</div>
                <div class="status-label">Held</div>
              </div>
            </div>
          </div>

          <!-- Regular Rooms Layout -->
          <div v-else class="status-grid">
            <div class="status-card status-card--total">
              <div class="status-icon">
                <img src="@/assets/purple-bed.png" alt="Total rooms" />
              </div>
              <div class="status-info">
                <div class="status-value">{{ formatNumber(space.spaceTotalRoom) }}</div>
                <div class="status-label">Total Rooms</div>
              </div>
            </div>

            <div class="status-card status-card--available">
              <div class="status-icon">
                <img src="@/assets/green-bed.png" alt="Available rooms" />
              </div>
              <div class="status-info">
                <div class="status-value">{{ formatNumber(space.statusCounts.AVAILABLE) }}</div>
                <div class="status-label">Available</div>
              </div>
            </div>

            <div class="status-card status-card--occupied">
              <div class="status-icon">
                <img src="@/assets/red-bed.png" alt="Occupied rooms" />
              </div>
              <div class="status-info">
                <div class="status-value">{{ formatNumber(space.statusCounts.BOOKED) }}</div>
                <div class="status-label">Occupied</div>
              </div>
            </div>

            <div class="status-card status-card--held">
              <div class="status-icon">
                <img src="@/assets/orange-bed.png" alt="Held rooms" />
              </div>
              <div class="status-info">
                <div class="status-value">{{ formatNumber(space.statusCounts.HELD) }}</div>
                <div class="status-label">Held</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <div class="empty-state-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7Z" stroke="currentColor" stroke-width="2"/>
            <path d="M8 11H16M8 15H12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <h3 class="empty-state-title">No Data Available</h3>
        <p class="empty-state-message">There are currently no room spaces configured for this property.</p>
        
        <div class="cards-grid cards-grid--empty">
          <div class="metric-card metric-card--total metric-card--empty">
            <div class="card-icon">
              <img src="@/assets/purple-bed.png" alt="Total rooms icon" class="icon-image" />
            </div>
            <div class="card-content">
              <div class="metric-value">0</div>
              <div class="metric-label">Total Rooms</div>
            </div>
          </div>

          <div class="metric-card metric-card--available metric-card--empty">
            <div class="card-icon">
              <img src="@/assets/green-bed.png" alt="Available rooms icon" class="icon-image" />
            </div>
            <div class="card-content">
              <div class="metric-value">0</div>
              <div class="metric-label">Available Rooms</div>
            </div>
          </div>

          <div class="metric-card metric-card--occupied metric-card--empty">
            <div class="card-icon">
              <img src="@/assets/red-bed.png" alt="Occupied rooms icon" class="icon-image" />
            </div>
            <div class="card-content">
              <div class="metric-value">0</div>
              <div class="metric-label">Occupied Rooms</div>
            </div>
          </div>

          <div class="metric-card metric-card--held metric-card--empty">
            <div class="card-icon">
              <img src="@/assets/orange-bed.png" alt="Held rooms icon" class="icon-image" />
            </div>
            <div class="card-content">
              <div class="metric-value">0</div>
              <div class="metric-label">Held Rooms</div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { readSessionToken } from '@/utilities/sessionTokenStorage';

export default {
  name: 'ObjectMainPage',
  props: {
    objectSpaces: {
      type: Array,
      required: true,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      required: true,
      default: false
    },
  },
  data() {
    return {
      objectName: '',
      adminProfileData: {},
      isRefreshing: false,
      chartView: 'occupancy',
      roomTypeMapping: {
        "1S": '1 Sharing',
        "2S": '2 Sharing',
        "3S": '3 Sharing',
        "4S": '4 Sharing',
        "5S": '5 Sharing',
        "6S": '6 Sharing',
      },
      dashboardData: {
        totalInventory: 0,
        totalOnboarded: 0,
        activeOnboarded: 0,
        totalRevenue: "0",
        occupancyRate: "0.00",
        weeklyGraph: []
      },
      isLoadingDashboard: false,
    };
  },
  computed: {
    // Make chart data reactive to chartView changes
    currentChartData() {
      return this.getChartData();
    },
    
    currentChartLabels() {
      return this.getChartLabels();
    }
  },
  
  mounted() {
    this.objectName = this.$route.params?.objectName || '';
    const adminProfileData = localStorage.getItem('ADMIN_PROFILE_DATA');
    this.adminProfileData = adminProfileData ? JSON.parse(adminProfileData) : {};
    this.fetchDashboardData();
  },
  methods: {
    async fetchDashboardData() {
      this.isLoadingDashboard = true;
      try {
        const token = readSessionToken();
        if (!token) {
          console.error('No authentication token found');
          return;
        }

        const response = await apiService.getObjectDashboard(this.$route.params.id);

        if (response.data) {
          this.dashboardData = {
            totalInventory: response.data.totalInventory || 0,
            totalOnboarded: response.data.totalOnboarded || 0,
            activeOnboarded: response.data.activeOnboarded || 0,
            totalRevenue: response.data.totalRevenue || "0",
            occupancyRate: response.data.occupancyRate || "0.00",
            weeklyGraph: response.data.weeklyGraph || []
          };
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Keep default values on error
      } finally {
        this.isLoadingDashboard = false;
      }
    },
    
    goBack() {
      this.$router.go(-1);
    },
    
    getCategoryTitle(space) {
      if (space.objectType === 'HOTEL') {
        return space.spaceCategory || 'Hotel Room';
      } else if (space.objectType === 'HOUSE') {
        return space.spaceType || 'House Room';
      } else if (space.objectType === 'HOSTEL') {
        return this.roomTypeMapping[space.spaceType] || space.spaceType || 'Hostel Room';
      }
      return 'Room';
    },
    
    isDormitoryOrHostel(space) {
      return space.spaceCategory === 'DORMITORY' || space.objectType === 'HOSTEL';
    },
    
    formatNumber(value) {
      if (value == null || value === undefined) return '0';
      return Number(value).toLocaleString();
    },

    // Dashboard Analytics Methods - Using API Data
    calculateTotalRooms() {
      return this.dashboardData.totalInventory.toLocaleString();
    },

    calculateOccupancyRate() {
      const rate = parseFloat(this.dashboardData.occupancyRate) || 0;
      return Math.round(rate);
    },

    calculateRevenue() {
      const revenue = parseFloat(this.dashboardData.totalRevenue) || 0;
      return revenue.toLocaleString();
    },



    async refreshData() {
      this.isRefreshing = true;
      try {
        await this.fetchDashboardData();
        this.$emit('refresh-data');
      } finally {
        this.isRefreshing = false;
      }
    },

    exportData() {
      const data = {
        totalInventory: this.dashboardData.totalInventory,
        totalOnboarded: this.dashboardData.totalOnboarded,
        activeOnboarded: this.dashboardData.activeOnboarded,
        occupancyRate: this.dashboardData.occupancyRate + '%',
        revenue: '₹' + this.calculateRevenue(),
        weeklyGraph: this.dashboardData.weeklyGraph,
        exportDate: new Date().toISOString(),
        spaces: this.objectSpaces
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `property-analytics-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    // Dynamic Trend Calculations
    // eslint-disable-next-line no-unused-vars
    getTrendDirection(_metric) {
      // For now, return 'up' as default since we don't have historical comparison data
      // This can be enhanced when historical data is available
      // Parameter is kept for template compatibility but not used yet
      return 'up';
    },

    getTrendClass(metric) {
      return this.getTrendDirection(metric) === 'up' ? 'kpi-trend--up' : 'kpi-trend--down';
    },

    getTrendPercentage(metric) {
      const current = this.getCurrentMetricValue(metric);
      const expected = this.getExpectedMetricValue(metric);
      
      if (expected === 0) return '0.0';
      
      const change = ((current - expected) / expected) * 100;
      const prefix = change >= 0 ? '+' : '';
      return prefix + Math.abs(change).toFixed(1);
    },

    getCurrentMetricValue(metric) {
      switch (metric) {
        case 'inventory':
          return this.dashboardData.totalInventory || 0;
        case 'occupancy':
          return this.calculateOccupancyRate();
        case 'revenue':
          return parseFloat(this.dashboardData.totalRevenue) || 0;
        default:
          return 0;
      }
    },

    getExpectedMetricValue(metric) {
      // Expected values based on industry standards or historical performance
      const totalRooms = this.dashboardData.totalInventory || 1;
      
      switch (metric) {
        case 'inventory':
          return totalRooms * 0.95; // Expect 95% of current as baseline
        case 'occupancy':
          return 70; // Industry standard occupancy target
        case 'revenue':
          return totalRooms * 2000 * 30; // Expected revenue per room (monthly)
        default:
          return 0;
      }
    },

    // Dynamic Chart Data - Using API weeklyGraph
    getChartData() {
      if (!this.dashboardData.weeklyGraph || this.dashboardData.weeklyGraph.length === 0) {
        return this.getEmptyChartData();
      }

      if (this.chartView === 'occupancy') {
        return this.getOccupancyChartData();
      } else {
        return this.getRevenueChartData();
      }
    },

    getChartLabels() {
      if (!this.dashboardData.weeklyGraph || this.dashboardData.weeklyGraph.length === 0) {
        return [];
      }
      
      return this.dashboardData.weeklyGraph.map((item, index) => {
        if (item.week) {
          // Format YYYYWW to readable format (e.g., 202526 -> "W26")
          const week = item.week % 100;
          return `W${week}`;
        }
        return `Week ${index + 1}`;
      });
    },

    getOccupancyChartData() {
      const weeklyData = this.dashboardData.weeklyGraph || [];
      
      if (weeklyData.length === 0) {
        return [];
      }

      // Find max occupancy for scaling
      const maxOccupancy = Math.max(...weeklyData.map(item => item.occupancyCount || 0), 1);
      
      return weeklyData.map((item, index) => {
        const occupancy = item.occupancyCount || 0;
        const height = maxOccupancy > 0 ? Math.max(5, (occupancy / maxOccupancy) * 100) : 5;
        const weekLabel = item.week ? `W${item.week % 100}` : `Week ${index + 1}`;
        
        return {
          height: height,
          color: 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
          label: weekLabel,
          value: occupancy.toString()
        };
      });
    },

    getRevenueChartData() {
      const weeklyData = this.dashboardData.weeklyGraph || [];
      
      if (weeklyData.length === 0) {
        return [];
      }

      // Find max revenue for scaling
      const revenues = weeklyData.map(item => parseFloat(item.revenue || 0));
      const maxRevenue = Math.max(...revenues, 1);
      
      return weeklyData.map((item, index) => {
        const revenue = parseFloat(item.revenue || 0);
        const height = maxRevenue > 0 ? Math.max(5, (revenue / maxRevenue) * 100) : 5;
        const weekLabel = item.week ? `W${item.week % 100}` : `Week ${index + 1}`;
        
        return {
          height: height,
          color: 'linear-gradient(180deg, #f59e0b 0%, #d97706 100%)',
          label: weekLabel,
          value: '₹' + revenue.toLocaleString()
        };
      });
    },

    getEmptyChartData() {
      return [];
    }
  },
};
</script>

<style scoped>
/* Modern Dashboard Styles */
.dashboard-container {
  min-height: 100vh;
  background: #f8fafc;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  color: #1e293b;
}

/* Header Styles */
.dashboard-header {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 20px 32px;
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.action-btn:hover {
  background: #e2e8f0;
  color: #334155;
  transform: translateY(-1px);
}

.action-btn:active {
  transform: translateY(0);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.02em;
  text-align: left;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  font-weight: 400;
  text-align: left;
}

.back-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #f1f5f9;
  color: #475569;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.back-button:hover {
  background: #e2e8f0;
  color: #334155;
  transform: translateX(-2px);
}

.back-button:active {
  transform: translateX(0);
}

/* Main Content */
.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px;
}

/* Metrics Section */
.metrics-section {
  margin-bottom: 32px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
}

.metric-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.metric-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--accent-color);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.metric-card:hover::before {
  transform: scaleX(1);
}

.metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  border-color: #cbd5e1;
}

.metric-card--inventory {
  --accent-color: #6366f1;
}

.metric-card--occupancy {
  --accent-color: #10b981;
}

.metric-card--revenue {
  --accent-color: #f59e0b;
}

.metric-card--guests {
  --accent-color: #3b82f6;
}

.metric-header {
  margin-bottom: 16px;
}

.metric-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--accent-bg);
  color: var(--accent-color);
}

.metric-card--inventory .metric-icon-wrapper {
  --accent-bg: #eef2ff;
  --accent-color: #6366f1;
}

.metric-card--occupancy .metric-icon-wrapper {
  --accent-bg: #d1fae5;
  --accent-color: #10b981;
}

.metric-card--revenue .metric-icon-wrapper {
  --accent-bg: #fef3c7;
  --accent-color: #f59e0b;
}

.metric-card--guests .metric-icon-wrapper {
  --accent-bg: #dbeafe;
  --accent-color: #3b82f6;
}

.kpi-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.kpi-trend--up {
  background: rgba(22, 163, 74, 0.1);
  color: #16a34a;
}

.kpi-trend--down {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}

.metric-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.metric-value {
  font-size: 32px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.metric-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  letter-spacing: 0.01em;
  text-align: center;
}

/* Chart Section */
.chart-section {
  margin-bottom: 32px;
}

.chart-container {
  background: #ffffff;
  border-radius: 16px;
  padding: 28px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.chart-container:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f1f5f9;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px 0;
  letter-spacing: -0.01em;
  text-align: left;
}

.section-description {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  text-align: left;
}

.chart-toggle {
  display: flex;
  gap: 8px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 10px;
}

.toggle-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.toggle-btn:hover {
  color: #334155;
}

.toggle-btn.active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.chart-body {
  padding-top: 8px;
}

.chart-wrapper {
  position: relative;
}

.chart-bars {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  height: 240px;
  padding: 0 12px 24px;
  gap: 12px;
}

.chart-bar {
  flex: 1;
  min-width: 32px;
  background: linear-gradient(180deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 8px 8px 0 0;
  position: relative;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 8px;
}

.chart-bar:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(99, 102, 241, 0.3);
}

.bar-tooltip {
  position: absolute;
  bottom: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%) translateY(4px);
  background: #0f172a;
  color: #ffffff;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.bar-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #0f172a;
}

.chart-bar:hover .bar-tooltip {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.chart-axis {
  display: flex;
  justify-content: space-between;
  padding: 0 12px;
  margin-top: 8px;
}

.axis-label {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
  flex: 1;
  text-align: center;
}

/* Performance Metrics */
.metrics-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.metric-item {
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.metric-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.metric-name {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.metric-value {
  font-size: 18px;
  font-weight: 700;
  color: #1a202c;
}

.metric-progress {
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #5b2e8e, #7c3aed);
  border-radius: 4px;
  transition: width 0.8s ease;
}

.progress-bar.satisfaction {
  background: linear-gradient(90deg, #16a34a, #22c55e);
}

.progress-bar.conversion {
  background: linear-gradient(90deg, #0ea5e9, #3b82f6);
}

.metric-change {
  font-size: 12px;
  font-weight: 600;
}

.metric-change.positive {
  color: #16a34a;
}

.metric-change.negative {
  color: #dc2626;
}

/* Inventory Section */
.inventory-section {
  margin-bottom: 32px;
}

.section-header {
  margin-bottom: 24px;
}

.section-controls {
  display: flex;
  gap: 8px;
}

.view-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.view-toggle.active,
.view-toggle:hover {
  background: #5b2e8e;
  border-color: #5b2e8e;
  color: #ffffff;
}

.inventory-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.inventory-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.inventory-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-color: #cbd5e1;
}

.inventory-header {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
}

.inventory-title {
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 6px 0;
  letter-spacing: -0.01em;
  text-align: left;
}

.inventory-type {
  display: inline-block;
  padding: 4px 12px;
  background: #f1f5f9;
  color: #475569;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: left;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.status-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 16px;
}

.status-card:hover {
  background: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: #cbd5e1;
}

.status-card--total {
  border-left: 4px solid #6366f1;
}

.status-card--available {
  border-left: 4px solid #10b981;
}

.status-card--occupied {
  border-left: 4px solid #ef4444;
}

.status-card--held {
  border-left: 4px solid #f59e0b;
}

.status-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #ffffff;
  border-radius: 10px;
  flex-shrink: 0;
}

.status-icon img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.status-info {
  flex: 1;
  min-width: 0;
}

.status-value {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
  line-height: 1.2;
  letter-spacing: -0.01em;
  text-align: left;
}

.status-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  text-align: left;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 64px 32px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

.empty-state-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  background: #f8fafc;
  border-radius: 16px;
  margin-bottom: 20px;
  color: #94a3b8;
}

.empty-state-title {
  font-size: 20px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 8px 0;
  letter-spacing: -0.01em;
  text-align: left;
}

.empty-state-message {
  font-size: 14px;
  color: #64748b;
  margin: 0 0 32px 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  text-align: left;
}

/* Loading States */
.loading-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.space-category-skeleton {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e2e8f0;
}

.skeleton-title {
  height: 24px;
  background: #f1f5f9;
  border-radius: 8px;
  margin-bottom: 20px;
  width: 200px;
  position: relative;
  overflow: hidden;
}

.skeleton-title::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
  animation: shimmer 1.5s infinite;
}

.card-skeleton {
  height: 120px;
  background: #f1f5f9;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.card-skeleton::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .main-content {
    padding: 24px;
  }
  
  .metrics-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }
  
  .status-grid {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 12px;
  }
}

@media (max-width: 768px) {
  .dashboard-header {
    padding: 16px 20px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .header-left {
    width: 100%;
  }
  
  .header-actions {
    align-self: flex-end;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .page-subtitle {
    font-size: 14px;
  }
  
  .main-content {
    padding: 20px;
  }
  
  .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  
  .metric-value {
    font-size: 28px;
  }
  
  .status-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  
  .chart-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .chart-toggle {
    width: 100%;
  }
  
  .chart-bars {
    height: 200px;
  }
  
  .inventory-card {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .dashboard-header {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 18px;
  }
  
  .main-content {
    padding: 16px;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .metric-card {
    padding: 20px;
  }
  
  .metric-value {
    font-size: 28px;
  }
  
  .status-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .chart-container {
    padding: 20px;
  }
  
  .chart-bars {
    height: 180px;
    padding: 0 8px 20px;
  }
  
  .inventory-card {
    padding: 16px;
  }
}

/* Focus and Accessibility */
.back-button:focus {
  outline: 2px solid #5b2e8e;
  outline-offset: 2px;
}

.metric-card:focus-within {
  outline: 2px solid #5b2e8e;
  outline-offset: 2px;
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  
  .metric-card:hover {
    transform: none;
  }
  
  .space-category:hover {
    transform: none;
  }
}
</style>